import { useEffect } from 'react';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
}

const UTM_STORAGE_KEY = 'utm_params';

/**
 * Captures UTM parameters from URL and stores them in sessionStorage
 * for later use during signup
 */
export const captureUtmParams = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check if any UTM params exist in URL
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmContent = urlParams.get('utm_content');
  const utmTerm = urlParams.get('utm_term');
  
  // Only store if we have at least one UTM param
  if (utmSource || utmMedium || utmCampaign || utmContent || utmTerm) {
    const utmData: UtmParams = {
      utm_source: utmSource || undefined,
      utm_medium: utmMedium || undefined,
      utm_campaign: utmCampaign || undefined,
      utm_content: utmContent || undefined,
      utm_term: utmTerm || undefined,
      referrer: document.referrer || undefined,
      landing_page: window.location.pathname,
    };
    
    // Store in sessionStorage (persists for session, cleared on browser close)
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
  } else if (!sessionStorage.getItem(UTM_STORAGE_KEY)) {
    // If no UTM params but we have a referrer, store that
    if (document.referrer && !document.referrer.includes(window.location.hostname)) {
      const utmData: UtmParams = {
        referrer: document.referrer,
        landing_page: window.location.pathname,
      };
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    }
  }
};

/**
 * Retrieves stored UTM parameters
 */
export const getStoredUtmParams = (): UtmParams | null => {
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Clears stored UTM parameters (call after successful signup)
 */
export const clearUtmParams = (): void => {
  sessionStorage.removeItem(UTM_STORAGE_KEY);
};

/**
 * Maps UTM parameters to acquisition channel
 */
export const getChannelFromUtm = (params: UtmParams): string => {
  const source = params.utm_source?.toLowerCase();
  const medium = params.utm_medium?.toLowerCase();
  
  // Paid search
  if (medium === 'cpc' || medium === 'ppc' || source === 'google_ads' || source === 'bing_ads') {
    return 'paid_search';
  }
  
  // Paid social
  if (medium === 'paid_social' || medium === 'cpm' || 
      source === 'facebook' || source === 'instagram' || source === 'tiktok' || 
      source === 'linkedin' || source === 'twitter') {
    return medium === 'cpc' || medium === 'cpm' || medium === 'paid_social' ? 'paid_social' : 'referral';
  }
  
  // Email
  if (medium === 'email' || source === 'newsletter' || source === 'email') {
    return 'email';
  }
  
  // Affiliate
  if (medium === 'affiliate' || source === 'affiliate') {
    return 'affiliate';
  }
  
  // Content marketing
  if (medium === 'content' || medium === 'blog' || source === 'blog') {
    return 'content';
  }
  
  // Referral (from other sites)
  if (params.referrer && !params.utm_source) {
    const referrerHost = new URL(params.referrer).hostname;
    if (referrerHost.includes('google') || referrerHost.includes('bing') || referrerHost.includes('duckduckgo')) {
      return 'organic';
    }
    return 'referral';
  }
  
  // Organic search
  if (medium === 'organic' || source === 'google' || source === 'bing') {
    return 'organic';
  }
  
  // Direct (no params, no referrer)
  if (!source && !medium && !params.referrer) {
    return 'direct';
  }
  
  // Default to other
  return 'other';
};

/**
 * Hook to capture UTM parameters on component mount
 */
export const useUtmTracking = (): void => {
  useEffect(() => {
    captureUtmParams();
  }, []);
};

export default useUtmTracking;
